import type { Dictionary } from './en.ts';

export const fr: Dictionary = {
  common: {
    tagline: 'traducteur vocal en temps réel',
    interfaceLanguage: "Langue de l'interface",
  },

  login: {
    title: 'Connexion',
    lede: "Saisissez le code d'accès à la démo. Ce navigateur s'en souviendra pendant 12 heures.",
    submit: 'Se connecter',
    checking: 'Vérification…',
    wrong: 'Ce code est incorrect. Vérifiez-le et réessayez.',
    failed: 'Connexion impossible. Réessayez dans un instant.',
  },

  home: {
    title: 'Une conversation en deux langues',
    lede: "Chacun parle sa propre langue et entend l'autre traduite. Deux personnes par salon, sans inscription.",
    myLanguage: 'Ma langue',
    peerLanguage: 'Sa langue',
    accessCode: "Code d'accès à la démo",
    signOut: 'Se déconnecter',
    samePair: "Choisissez deux langues différentes — sinon il n'y a rien à traduire.",
    create: 'Créer un salon',
    creating: 'Création…',
    createFailed: "Le salon n'a pas pu être créé",

    readyTitle: 'Le salon est prêt',
    youHear: (language: string) => `Vous entendez : ${language}.`,
    theyHear: (language: string) => `L'autre personne entend : ${language}.`,
    inviteLabel: "Lien d'invitation — envoyez-le à l'autre personne",
    copy: 'Copier',
    copied: 'Copié',
    inviteNote:
      "Le lien est valable 15 minutes et ne laisse entrer qu'une seule personne. Ne le publiez nulle part — c'est la clé du salon.",
    sourceSubtitles: 'Afficher aussi les paroles originales',
    sourceSubtitlesOn:
      "Vous verrez ce que chacun a réellement dit, dans sa propre langue, à côté de la traduction. Le fournisseur le facture séparément : l'appel coûte donc plus cher.",
    sourceSubtitlesOff:
      "Seule la traduction est affichée. Rien n'est transcrit dans la langue d'origine et rien n'est facturé en plus.",
    sourceSubtitlesReady: 'Les paroles originales seront également affichées.',
    goToCheck: 'Vérifier mon son',
  },

  voice: {
    you: 'Vous',
    peer: "L'autre personne",
    language: 'Langue',
    provider: 'Synthèse vocale',
    openaiNote: 'voix intégrée, immédiate',
    elevenlabsNote: 'voix plus nette, commence à la fin de la phrase',
    noVoices: 'aucune voix pour cette langue',
    voice: 'Voix',
    builtIn: "Voix intégrée d'OpenAI",
    loading: 'Chargement des voix…',
    failed: 'Impossible de charger les voix. Réessayez plus tard.',
    hintBuiltIn: 'Une seule voix pour toutes les langues, comme avant.',
    hintLibrary: 'Une voix de la bibliothèque ElevenLabs.',
    groupVerified: 'Vérifiées pour cette langue',
    groupOther: 'Autres voix',
    accent: "Cette voix n'est pas vérifiée pour cette langue et peut avoir un accent.",
    noSample: "Cette voix n'a pas d'extrait",
    listen: 'Écouter',
    stop: 'Arrêter',
    needVoice:
      'Choisissez une voix pour chaque côté réglé sur ElevenLabs, ou remettez-le sur OpenAI.',
  },

  join: {
    checking: "Vérification de l'invitation…",
    title: 'Invitation à une conversation',
    youWillHear: (language: string) => `Vous entendrez : ${language}.`,
    theyHear: (language: string) => `L'autre personne entend : ${language}.`,
    connect: 'Rejoindre',
    connecting: 'Connexion…',
    full: 'Le salon est complet.',
    failedTitle: 'Impossible de rejoindre',
    createYourOwn: 'Créez votre propre salon',
    noInvite: 'Ce lien ne contient aucune invitation. Demandez-la de nouveau.',
    unavailable: "Ce salon n'est pas disponible",
    joinFailed: 'Échec de la connexion au salon',
  },

  check: {
    title: 'Vérification du son',
    lede: "Mettez un casque : sans casque, la traduction repart dans votre micro et l'autre personne entend un écho.",
    microphone: 'Microphone',
    defaultMic: 'Microphone par défaut',
    level: 'Niveau — dites quelque chose',
    applied: (features: string) => `Votre navigateur a appliqué : ${features}.`,
    appliedNone: 'aucun traitement',
    echoCancellation: "annulation d'écho",
    noiseSuppression: 'réduction du bruit',
    autoGain: 'gain automatique',
    measureNote: 'Mesurez avec le casque sur les oreilles.',
    testSound: 'Tester le son',
    testAgain: 'Tester le son à nouveau',
    start: "C'est bon, commencer",
    starting: 'Connexion…',
    micDenied:
      "L'accès au microphone a été refusé. Autorisez-le dans les réglages de votre navigateur et rechargez la page.",
    micNotFound: 'Aucun microphone détecté. Branchez-en un et rechargez la page.',
    micFailed: "Le microphone n'a pas pu être ouvert.",
  },

  call: {
    title: 'Conversation',
    timeLeft: (time: string) => `· ${time} restantes`,
    waitingPeer: "En attente de l'autre personne. Envoyez-lui le lien d'invitation.",
    autoplayBlocked: 'Votre navigateur a bloqué la lecture du son.',
    enableSound: 'Activer le son',
    lagging:
      'La traduction prend du retard. Vous pouvez relancer le traducteur — le fragment en cours sera perdu.',
    translatorStopped: (reason: string) =>
      `La traduction s'est arrêtée : ${reason}. L'appel continue — relancez le traducteur.`,
    translationVolume: 'Volume de la traduction',
    hearOriginal: 'Entendre la voix originale',
    muteMic: 'Couper le microphone',
    unmuteMic: 'Activer le microphone',
    restart: 'Relancer la traduction',
    asleep:
      'Personne n’a parlé depuis une minute, la traduction est donc en pause pour éviter des frais. Elle reprendra d’elle-même dès que votre interlocuteur parlera.',
    wake: 'Reprendre maintenant',
    leave: 'Quitter',
    peerMuted: "L'autre personne a coupé son microphone",

    metricConnect: 'Démarrage du traducteur',
    metricFirst: 'Première traduction',
    metricLag: 'Retard estimé',
    metricRtt: 'RTT vers le fournisseur',
    metricVoiceDelay: 'Délai de la synthèse vocale',
    metricCost: "Coût jusqu'ici (estimation)",
    costBreakdown: (openai: string, voice: string, credits: number) =>
      `OpenAI ${openai} · ElevenLabs ${voice} · crédits: ${credits}`,
    costNote: 'Prix publics réglés sur le serveur, pas la facture des fournisseurs.',
    metricVoiceLead: 'ElevenLabs face à OpenAI, moyenne',
    voiceLeadEarlier: (seconds: string) => `${seconds} s plus tôt`,
    voiceLeadLater: (seconds: string) => `${seconds} s plus tard`,
    iHear: (voice: string) => `J'entends : ${voice}`,
    peerHears: (voice: string) => `L'autre personne entend : ${voice}`,
    voiceFellBack: "OpenAI (ElevenLabs n'a pas répondu)",

    endedTitle: 'La conversation est terminée',
    endedNewRoom: 'Pour une nouvelle conversation, créez un nouveau salon.',
    newRoom: 'Nouveau salon',
    rateTitle: 'Évaluez la conversation',
  },

  closed: {
    'peer-left': "L'autre personne a mis fin à la conversation.",
    'time-limit': 'La limite de temps de la démo a été atteinte.',
    'peer-lost': "L'autre personne est restée absente trop longtemps.",
    'server-shutdown': 'Le serveur redémarre.',
    expired: 'Le salon a expiré.',
    unknown: 'La connexion a été fermée.',
    signalLost: 'Contact perdu avec le serveur. Créez un nouveau salon.',
  },

  status: {
    serverConnecting: 'Serveur : connexion',
    serverOpen: 'Serveur : connecté',
    serverReconnecting: 'Serveur : reconnexion',
    serverClosed: 'Serveur : déconnecté',

    peerWaiting: 'En face : en attente',
    peerConnecting: 'En face : connexion',
    peerConnected: 'En face : connecté',
    peerReconnecting: 'En face : rétablissement',
    peerFailed: 'En face : pas de connexion',
    peerGone: 'En face : parti',

    translatorIdle: 'Traduction pour moi : non démarrée',
    translatorConnecting: 'Traduction pour moi : démarrage',
    translatorReady: 'Traduction pour moi : en cours',
    translatorReconnecting: 'Traduction pour moi : rétablissement',
    translatorFailed: 'Traduction pour moi : échec',
    translatorClosed: 'Traduction pour moi : arrêtée',
    translatorAsleep: 'Traduction pour moi : en pause',

    peerTranslatorReady: "Traduction pour l'autre personne : en cours",
    peerTranslatorWaiting: "Traduction pour l'autre personne : en attente",
    peerTranslatorAsleep: "Traduction pour l'autre personne : en pause",
    relayed: 'Route : via TURN',
  },

  subtitles: {
    them: 'En face',
    you: 'Vous',
    themEmpty: "L'autre personne n'a encore rien dit",
    youEmpty: 'Dites quelque chose — vos mots et leur traduction apparaîtront ici',
  },

  rating: {
    accuracy: 'Précision de la traduction',
    voice: 'Naturel de la voix',
    comfort: 'Confort de la conversation',
    send: 'Envoyer les évaluations',
    thanks: 'Merci — les évaluations sont enregistrées.',
  },

  errors: {
    no_credit: "le compte OpenAI n'a plus de crédit — rechargez-le",
    rate_limited: 'le service de traduction nous limite — réessayez dans une minute',
    unconfigured: "aucune clé de traduction n'est configurée sur le serveur",
    unreachable: "le serveur n'a pas pu joindre le service de traduction",
    offline: 'impossible de joindre le serveur — vérifiez votre connexion',
    session_expired: 'la session de traduction a expiré — relancez la traduction',
    room_closed: 'ce salon est déjà fermé',
    bad_offer: 'le navigateur a produit une offre de connexion invalide',
    provider_refused: (status: number) =>
      `le service de traduction a refusé la connexion (${status})`,
    unknown: "la traduction n'a pas pu démarrer",
  },

  banner: {
    providerUnconfigured:
      "Aucune clé de fournisseur de traduction n'est configurée sur le serveur. Vous pouvez créer un salon, mais rien ne sera traduit.",
  },
};
